import type { OutreachCloseReason, OutreachLegacyCloseReason } from '@discovr/types';
import { fmtDeliverableType, isRecoveryConflictCode } from '@discovr/types';
import { SEND_ERROR_LABELS, SKIP_LABELS } from './outreachSendCopy';
import { GENERIC_ERROR_COPY, MORE_ERROR_COPY, OPAQUE_ERROR_COPY, contextCopy, type CopyFn, type ErrorContext } from './errorCopyByContext';

export { SEND_ERROR_LABELS, SKIP_LABELS };
export type { ErrorContext };

/** The thread ⋯ menu's Close conversation ▸ submenu labels. Legacy fact reasons
 *  read as Ghosted so an old closed row still labels. */
export const CLOSE_REASON_LABELS: Record<OutreachCloseReason | OutreachLegacyCloseReason, string> = {
  rejected: 'Rejected',
  not_interested: 'Not interested',
  ghosted: 'Ghosted',
  unresponsive: 'Ghosted',
  no_contact: 'Ghosted',
  brand_rejected: 'Brand declined',
};

/** A server code is useful to an operator even before a friendly label exists. */
export function sendErrorMessage(error: unknown): string {
  const code = codeOf(error);
  if (code !== undefined) return errorCopy(code, detailsOf(error));
  return error instanceof Error && error.message ? error.message : 'Send failed';
}

/** The server's error code, or undefined for a non-API error. */
export function codeOf(error: unknown): string | undefined {
  const code = typeof error === 'object' && error !== null ? (error as { code?: unknown }).code : undefined;
  return typeof code === 'string' ? code : undefined;
}

/** The HTTP status off an ApiError-shaped value, or undefined. */
export function statusOf(error: unknown): number | undefined {
  const status = typeof error === 'object' && error !== null ? (error as { httpStatus?: unknown }).httpStatus : undefined;
  return typeof status === 'number' ? status : undefined;
}

/** The server's `details` object, or undefined. */
export function detailsOf(error: unknown): Record<string, unknown> | undefined {
  const d = typeof error === 'object' && error !== null ? (error as { details?: unknown }).details : undefined;
  return typeof d === 'object' && d !== null ? (d as Record<string, unknown>) : undefined;
}

const NOT_NOW_COPY = "This creator can't take this action right now. Refresh to see their current state.";

/** Go sends only the roster status. `outreach` covers sourced, stopped, bounced, unsubscribed and removed rows alike, so it gets the neutral line. */
function notInConversationCopy(d?: Record<string, unknown>): string {
  const status = typeof d?.status === 'string' ? d.status : undefined;
  if (status === 'removed_by_admin') return 'This creator was removed from the campaign.';
  if (status?.startsWith('rejected_')) return 'This creator was rejected.';
  if (status === 'pending' || status === 'accepted_by_brand') return 'This creator is not in outreach, so there is no agreed rate to change.';
  if (status === 'accepted_by_admin' || status === 'accepted_by_creator') return 'This creator has already been accepted.';
  if (status === '') return 'Nothing has been sent to this creator yet.';
  return NOT_NOW_COPY;
}

const STALE_SELECTION_COPY = 'Some selected creators are no longer in this campaign. Refresh and try again.';

/** Copy for the 2.5 negotiation/draft/follow-up routes' rejection codes
 *  (rulings 46-58) — a second vocabulary alongside SEND_ERROR_LABELS' send
 *  codes, since the two route families don't share every code. */
const NEGOTIATION_ERROR_LABELS: Record<string, CopyFn> = {
  CONTACT_LOOKUP_UNSUPPORTED: () => 'Contact lookup is Instagram only.',
  KIND_NOT_SUPPORTED: () => 'That kind of message is not supported here.',
  ENTRY_NOT_IN_CONVERSATION: notInConversationCopy,
  NOTHING_TO_CONFIRM: () => 'Nothing to confirm — the creator has not quoted a rate.',
  NO_THREAD: () => 'Nothing has been sent to this creator yet.',
  RATES_REQUIRED: (d) => {
    const unbound = Array.isArray(d?.unbound) ? (d.unbound as number[]) : [];
    if (unbound.length === 0) return 'Add at least one rate.';
    const amounts = unbound.map((n) => n.toLocaleString('en-IN')).join(' and ');
    return `We couldn't tell which deliverable ${amounts} ${unbound.length === 1 ? 'is' : 'are'} for — pick one for each amount.`;
  },
  INVALID_RATES: (d) => `Check the rates${d?.reason ? ` — ${String(d.reason)}` : ''}.`,
  MULTIPLE_MAIN_DELIVERABLES: () => 'A deal has one main deliverable plus add-ons. Keep one main and agree again.',
  UNKNOWN_DELIVERABLE: (d) => {
    const allowed = (Array.isArray(d?.allowed) ? (d.allowed as string[]) : []).map(fmtDeliverableType);
    const what = typeof d?.deliverable === 'string' ? fmtDeliverableType(d.deliverable) : 'That deliverable';
    return `${what} is not offered on this platform.${allowed.length ? ` Allowed: ${allowed.join(', ')}.` : ''}`;
  },
  ENTRY_NOT_AGREED: () => 'Agree a rate before sending to the brand.',
  BRAND_NOT_APPROVED: () => 'The brand has not approved this rate yet — the final confirmation goes out after they do.',
  ALREADY_CONFIRMED: () => 'The final confirmation for this approval was already sent.',
  ENTRY_PAST_BRAND_APPROVAL: () => 'The brand has already moved past approving this rate.',
  MESSAGE_ALREADY_QUEUED: () => 'A message to this creator is already queued — let it send, or cancel it, first.',
  ENTRY_NOT_WITH_BRAND: () => 'This creator is not waiting on a brand decision.',
  NOTE_TOO_LONG: () => 'Keep the note under 512 characters.',
  OVERRIDE_NOT_APPLICABLE: () =>
    "Send anyway doesn't apply to this message any more. It may have changed since it was held. Refresh the thread to see where it stands.",
  ACKNOWLEDGEMENT_DRAFT_NOT_AVAILABLE: () => 'There is nothing to acknowledge yet. A draft is only available once a rate is agreed, until the brand decides.',
  ENTRY_NOT_CLOSED: () => 'Only a closed conversation can be reopened.',
  DRAFT_TOO_LARGE: () => 'This draft is too long to save. Shorten it and try again.',
  TOO_MANY_RATES: (d) => (typeof d?.max === 'number' ? `A draft can carry at most ${d.max} rates.` : 'This draft has too many rates.'),
  ENTRY_NOT_REOPENABLE: () => 'This conversation is not stopped, so there is nothing to reopen.',
  REOPEN_SUPPRESSED: () => 'This creator opted out ("Do not contact"), so this conversation cannot be reopened.',
  ENTRY_NOT_UNSUBSCRIBED: () => 'This conversation is no longer held by an unsubscribe. Refresh to see where it stands.',
  NOT_UNSUBSCRIBE_BLOCKED: (d) => {
    if (d?.reason === 'creator_opt_out') return 'The creator opted out themselves, through the unsubscribe link or their mail app. That block stays.';
    if (d?.reason === 'complaint') return 'The creator reported spam. That block stays.';
    if (d?.reason === 'bounce') return 'This address bounced. Add a different email for this creator, then reopen.';
    return 'This block was not set by the agent reading a reply, so it stays. Remove it from the suppression list if it is wrong.';
  },
  BOUNCE_REOPEN_NEEDS_NEW_EMAIL: (d) => {
    if (d?.reason === 'same_address') return 'That address already bounced. Add a different email for this creator, then reopen.';
    if (d?.reason === 'suppressed') return "The creator's new address is on the do-not-contact list. Use another address, then reopen.";
    return 'This creator has no email. Add a new one, then reopen.';
  },
  REOPEN_REMOVED_AFTER_BRAND: () => 'This creator reached the brand before they were removed, so they cannot be reinstated into outreach.',
  ENTRY_ALREADY_CLOSED: () => 'This conversation is already closed.',
  ENTRY_NOT_IN_OUTREACH: () => 'This creator is no longer in outreach.',
  ENTRY_WITH_BRAND: () => 'This creator is with the brand now. Ask the brand to decide first, or reject it from the brand dashboard.',
  ENTRY_REMOVED: () => 'This creator was removed.',
  INVALID_ENTRY_IDS: () => STALE_SELECTION_COPY,
  ENTRY_NOT_IN_CAMPAIGN: () => STALE_SELECTION_COPY,
  ENTRY_ERASED: () => 'This creator was erased and cannot be reopened.',
  FOLLOWUPS_ALREADY_PAUSED: () => 'Follow-ups are already paused.',
  FOLLOWUPS_NOT_PAUSED: () => 'Follow-ups are not paused.',
  NO_SEQUENCE: () => 'Follow-ups only run for a contacted creator.',
  SEQUENCE_COMPLETE: () => 'Every follow-up in the sequence has been sent.',
  DRAFT_NOT_FOUND: () => 'That draft is gone. Refresh to see the current drafts.',
  DRAFT_NOT_PENDING: () => 'That draft was already approved or cancelled.',
  STALE_DRAFT: () => 'The creator replied after this draft was written — read the reply first.',
  NO_SUGGESTION: () => 'No suggested reply for the latest message.',
  NO_FOLLOWUP_DUE: () => 'No follow-up is due right now.',
  AGENT_UNAVAILABLE: () => "The assistant isn't available in this environment.",
  TEMPLATE_STEP_NOT_FOUND: () => "That step isn't in this creator's sequence.",
  NO_SEQUENCE_SNAPSHOT: () => "This creator's sequence has no such step yet.",
  AI_COMPOSE_FAILED: () => "The assistant isn't working for now.",
  AI_NOT_CONFIGURED: () => "The assistant isn't set up in this environment.",
  HELP_ME_WRITE_INTERRUPTED: () => 'The connection dropped. Nothing was kept.',
  REASON_TOO_LONG: () => 'Keep the reason under 200 characters.',
};

const own = <T,>(map: Record<string, T>, code: string): T | undefined => (Object.hasOwn(map, code) ? map[code] : undefined);

/** The code's own copy in this context (owner 2026-10-12: a save and a send word the same code differently). */
function specificCopy(code: string, context: ErrorContext): CopyFn | undefined {
  const sendLabel = own(SEND_ERROR_LABELS, code);
  return contextCopy(code, context) ?? own(NEGOTIATION_ERROR_LABELS, code) ?? own(MORE_ERROR_COPY, code) ?? (sendLabel === undefined ? undefined : () => sendLabel);
}

/** Copy for a server code in a context ('send' unless the caller was saving or editing a template), then Go's
 *  generic codes, then the raw code. 'UNKNOWN' is apiClient's own sentinel for a response with no error envelope
 *  (a network failure, a bare 5xx) — never a real business code, so it gets the generic copy rather than leaking
 *  the sentinel itself into a toast. */
export function errorCopy(code: string | undefined, details?: Record<string, unknown>, context: ErrorContext = 'send'): string {
  if (!code || code === 'UNKNOWN') return 'Something went wrong — try again.';
  // No response at all — including a WAF/ALB 403, which carries no CORS headers and so looks like this to the browser.
  // It may still have reached the server (a dropped response), so a retry could do it twice.
  if (code === 'NETWORK_ERROR') return "Couldn't reach the server, so we can't tell whether that went through. Refresh and check before you try again.";
  const fn = specificCopy(code, context) ?? opaqueCopy(code);
  if (fn) return fn(details);
  return own(GENERIC_ERROR_COPY, code) ?? code;
}

/** Whether errorCopy has real copy for this code, rather than falling back to the raw code. */
export function hasErrorCopy(code: string, context: ErrorContext = 'send'): boolean {
  if (code === 'UNKNOWN' || code === 'NETWORK_ERROR') return true;
  return specificCopy(code, context) !== undefined || opaqueCopy(code) !== undefined || Object.hasOwn(GENERIC_ERROR_COPY, code);
}

/** For saves whose server sentences used to be shown raw (templates, settings): the app's copy when the code has
 *  some, else the server sentence without its `CODE: ` prefix, else the caller's line. */
export function apiErrorCopy(error: unknown, fallback: string, context: ErrorContext = 'send'): string {
  const code = codeOf(error);
  if (code === 'UNKNOWN' || code === 'NETWORK_ERROR') return errorCopy(code);
  if (code === undefined) return stripCode(error) || fallback;
  if (specificCopy(code, context) || opaqueCopy(code)) return errorCopy(code, detailsOf(error), context);
  return stripCode(error, code) || own(GENERIC_ERROR_COPY, code) || fallback;
}

function opaqueCopy(code: string): CopyFn | undefined {
  const copy = own(OPAQUE_ERROR_COPY, code);
  return copy === undefined ? undefined : () => copy;
}

/** The server sentence without the `CODE: ` prefix rethrowWithCode stamps on (the error's own code when known). */
function stripCode(error: unknown, code?: string): string {
  if (!(error instanceof Error)) return '';
  const prefix = code ? `${code}:` : undefined;
  const message = prefix && error.message.startsWith(prefix) ? error.message.slice(prefix.length) : error.message.replace(/^[A-Z][A-Z0-9_]{2,}:/, '');
  const trimmed = message.trim();
  return trimmed === code ? '' : trimmed;
}

/** Recovery conflicts carry operator-specific server copy, such as the next
 * daily-cap slot. Keep that sentence intact rather than collapsing it to a
 * generic code label. */
export function recoveryErrorCopy(error: unknown): string {
  const code = codeOf(error);
  if (isRecoveryConflictCode(code)) {
    const message = error instanceof Error ? error.message.replace(new RegExp(`^${code}:\\s*`), '').trim() : '';
    if (message) return message;
  }
  return errorCopy(code, detailsOf(error));
}

/** Copy for the acknowledgement-draft read's rejections (its own 409 code lives in NEGOTIATION_ERROR_LABELS). */
export function acknowledgementDraftErrorCopy(code: string | undefined, details?: Record<string, unknown>): string {
  return errorCopy(code, details);
}

/** Copy for the counter-draft compose's rejections (same suggest-reply route, codes already in NEGOTIATION_ERROR_LABELS). */
export function counterDraftErrorCopy(code: string | undefined, details?: Record<string, unknown>): string {
  return errorCopy(code, details);
}

const PERMISSION_DENIED = "You don't have permission to do that";
const ENTRY_UNAVAILABLE = 'This creator is no longer available to you';

function hasLabel(code: string): boolean {
  return specificCopy(code, 'send') !== undefined;
}

/** Copy for a row/thread entry action. Keyed on the status first: the authz
 *  403 body sits outside the `{error:{…}}` envelope, so its code arrives as
 *  UNKNOWN, and entry-instance authz answers 404 for an entry the caller can't see. */
export function entryActionErrorCopy(error: unknown): string {
  const code = codeOf(error);
  const status = statusOf(error);
  if (status === 403) return PERMISSION_DENIED;
  if (status === 404 && !(code && hasLabel(code))) return ENTRY_UNAVAILABLE;
  return errorCopy(code, detailsOf(error));
}


const HELP_ME_WRITE_REASON_COPY: Record<string, string> = {
  agent_busy: 'The assistant is busy. Try again in a moment.',
  agent_timeout: 'The assistant took too long. Try again.',
  deadline_exceeded: 'The assistant took too long. Try again.',
  llm_timeout: 'The assistant took too long. Try again.',
  agent_unreachable: "Couldn't reach the assistant. Try again in a moment.",
  agent_bad_response: 'The assistant sent something unreadable. Try again.',
  agent_auth: "The assistant isn't set up correctly in this environment.",
  agent_rejected: "The assistant couldn't use this request. Edit your prompt and try again.",
  agent_unconfigured: "The assistant isn't set up in this environment.",
  request_canceled: 'The request was cancelled. Nothing was kept.',
  invalid_draft: "The assistant's draft was empty or unusable. Try again.",
  'draft_rejected:money': 'The draft mentioned an amount that is not in your prompt, so it was dropped. Put the figure in your prompt, or ask without one.',
  'draft_rejected:claims': 'The draft made a promise or claim we cannot back, so it was dropped. Rephrase your prompt.',
  'draft_rejected:link': 'The draft contained a link that is not in your prompt or the campaign, so it was dropped.',
  'draft_rejected:length': 'The draft was too long, so it was dropped. Ask for something shorter.',
  'llm_failed:transport': 'The assistant had a connection problem. Try again.',
  'llm_invalid_output:schema': 'The assistant answered in an unusable form. Try again.',
  'llm_invalid_output:empty': 'The assistant wrote nothing. Try again.',
};

const HELP_ME_WRITE_GENERIC = "The assistant couldn't write that. Try again.";

/** Copy for a failed Help me write, whichever way it failed: a pre-stream problem and a mid-stream `error` event
 *  both arrive as code AI_COMPOSE_FAILED with details.reason. Never shows a raw machine code. */
export function helpMeWriteErrorCopy(error: unknown): string {
  const code = codeOf(error);
  const reason = detailsOf(error)?.reason;
  if (code === 'AI_COMPOSE_FAILED' && typeof reason === 'string') {
    return own(HELP_ME_WRITE_REASON_COPY, reason) ?? own(HELP_ME_WRITE_REASON_COPY, reason.split(':')[0] ?? '') ?? HELP_ME_WRITE_GENERIC;
  }
  if (code !== undefined && hasErrorCopy(code)) return errorCopy(code, detailsOf(error));
  return HELP_ME_WRITE_GENERIC;
}

/** Retry is offered unless the server said it cannot help. */
export function helpMeWriteRetryable(error: unknown): boolean {
  return detailsOf(error)?.retryable !== false;
}
